import { useEffect } from 'react';
import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { useSocket } from '../context/SocketProvider';
import { fetchStudents, Student } from '../api/students';

const DEFAULT_DISMISS_EXPIRE_MINUTES = 2;

const parseExpiryMinutes = (): number => {
  const raw = process.env.REACT_APP_DISMISS_EXPIRE_MINUTES;
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_DISMISS_EXPIRE_MINUTES;
};

const DISMISSAL_EXPIRY_MS = parseExpiryMinutes() * 60 * 1000;

const sortStudents = (students: Student[]): Student[] =>
  [...students].sort(
    (a, b) => new Date(b.checked_in_at).getTime() - new Date(a.checked_in_at).getTime()
  );

const pruneDismissedStudents = (students: Student[], now: number): Student[] =>
  students.filter((student) => {
    if (!student.dismissed_at) return true;
    const expiry = new Date(student.dismissed_at).getTime() + DISMISSAL_EXPIRY_MS;
    return expiry > now;
  });

const transformStudents = (students: Student[], now: number = Date.now()): Student[] =>
  sortStudents(pruneDismissedStudents(students, now));

export const useStudents = (): UseQueryResult<Student[], Error> => {
  const socketContext = useSocket();
  const socket = socketContext?.socket ?? null;
  const queryClient = useQueryClient();

  const queryResult = useQuery<Student[], Error>({
    queryKey: ['students'],
    queryFn: fetchStudents,
    refetchOnWindowFocus: false,
    select: (students) => transformStudents(students),
  });

  // Socket sync
  useEffect(() => {
    if (!socket) return undefined;

    const handleAdded = (student: Student) => {
      console.debug('[useStudents] student_added received', { id: student.id });
      queryClient.setQueryData<Student[]>(['students'], (prev = []) => {
        const exists = prev.some((s) => s.id === student.id);
        const next = exists
          ? prev.map((s) => (s.id === student.id ? student : s))
          : [...prev, student];
        return transformStudents(next);
      });
    };

    const handleDismissed = (student: Student) => {
      console.debug('[useStudents] student_dismissed received', { id: student.id });
      queryClient.setQueryData<Student[]>(['students'], (prev = []) => {
        const next = prev.map((s) => (s.id === student.id ? student : s));
        return transformStudents(next);
      });
    };

    socket.on('student_added', handleAdded);
    socket.on('student_dismissed', handleDismissed);

    return () => {
      socket.off('student_added', handleAdded);
      socket.off('student_dismissed', handleDismissed);
      console.debug('[useStudents] socket listeners cleaned up');
    };
  }, [queryClient, socket]);

  // Periodically prune dismissed students that have expired.
  useEffect(() => {
    const intervalId = window.setInterval(() => {
      queryClient.setQueryData<Student[]>(['students'], (prev = []) => {
        const pruned = transformStudents(prev);
        if (pruned.length !== prev.length) {
          console.debug('[useStudents] Periodic prune removed dismissed students');
        }
        return pruned;
      });
    }, 30_000);

    return () => window.clearInterval(intervalId);
  }, [queryClient]);

  return queryResult;
};
